import { differenceInDays } from 'date-fns'
import { type JobStatus } from '@/features/jobs/job-status'

/**
 * The result of the deadline display computation.
 */
export interface DeadlineDisplay {
  proximity: DeadlineProximity
  friendlyText: string
}

/**
 * The props we need to compute the deadline display.
 */
export interface DeadlineDisplayInput {
  status: JobStatus
  dateDeadline?: Date | null
}

/**
 * How close we are to the deadline, in relative terms.
 */
export type DeadlineProximity = 'NotApplicable' | 'PrettyFar' | 'Far' | 'Close'

/**
 * Computes the deadline display properties.
 */
export function computeDeadlineDisplay(
  input: DeadlineDisplayInput,
  now = new Date(),
): DeadlineDisplay {
  const daysUntilNow = input.dateDeadline
    ? differenceInDays(input.dateDeadline, now)
    : null
  if (daysUntilNow === null || input.status === 'UnderConstruction') {
    return { proximity: 'NotApplicable', friendlyText: 'N/A' }
  }

  const dueIn = `Due in ${daysUntilNow} days`
  // The trick is to put the most specific condition first
  // in the cases.
  switch (true) {
    case daysUntilNow === 1:
      return {
        proximity: 'Close',
        friendlyText: 'Due tomorrow',
      }
    case daysUntilNow === 0:
      return {
        proximity: 'Close',
        friendlyText: 'Due today',
      }
    case daysUntilNow === -1:
      return {
        proximity: 'Close',
        friendlyText: 'Due yesterday',
      }
    case daysUntilNow < -1:
      return {
        proximity: 'Close',
        friendlyText: `Due ${Math.abs(daysUntilNow)} days ago`,
      }
    case daysUntilNow < 7:
      return {
        proximity: 'Close',
        friendlyText: dueIn,
      }
    case daysUntilNow < 30:
      return {
        proximity: 'Far',
        friendlyText: dueIn,
      }
    default:
      return { proximity: 'PrettyFar', friendlyText: dueIn }
  }
}
